import { useState, useEffect, useRef } from "react";

// ── GLOBAL STYLES ─────────────────────────────────────────────────────────────
const G = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&family=JetBrains+Mono:wght@400;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg:       #03050D;
      --surface:  #080D1A;
      --card:     #0C1426;
      --card2:    #101B32;
      --border:   rgba(99,179,237,0.1);
      --border2:  rgba(99,179,237,0.18);
      --primary:  #63B3ED;
      --violet:   #9F7AEA;
      --emerald:  #48BB78;
      --amber:    #F6AD55;
      --rose:     #FC8181;
      --text:     #E2ECF8;
      --muted:    #6B8CAE;
      --h:        'Syne', sans-serif;
      --b:        'DM Sans', sans-serif;
      --m:        'JetBrains Mono', monospace;
    }

    html { scroll-behavior: smooth; }

    body {
      background: var(--bg);
      color: var(--text);
      font-family: var(--b);
      overflow-x: hidden;
      line-height: 1.6;
    }

    ::selection { background: var(--violet); color: #fff; }
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-track { background: var(--bg); }
    ::-webkit-scrollbar-thumb { background: var(--violet); border-radius: 2px; }

    /* mesh bg */
    body::before {
      content: '';
      position: fixed; inset: 0; z-index: 0; pointer-events: none;
      background:
        radial-gradient(ellipse 80% 50% at 20% 10%, rgba(99,179,237,0.07) 0%, transparent 60%),
        radial-gradient(ellipse 60% 40% at 80% 80%, rgba(159,122,234,0.07) 0%, transparent 60%),
        radial-gradient(ellipse 40% 30% at 50% 50%, rgba(72,187,120,0.03) 0%, transparent 70%);
    }

    /* subtle grid */
    body::after {
      content: '';
      position: fixed; inset: 0; z-index: 0; pointer-events: none;
      background-image:
        linear-gradient(rgba(99,179,237,0.025) 1px, transparent 1px),
        linear-gradient(90deg, rgba(99,179,237,0.025) 1px, transparent 1px);
      background-size: 60px 60px;
    }

    .page { position: relative; z-index: 2; }

    /* ── NAV ── */
    nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 200;
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 2.5rem; height: 68px;
      background: rgba(3,5,13,0.8);
      backdrop-filter: blur(24px);
      border-bottom: 1px solid var(--border);
      transition: all 0.3s;
    }

    nav.scrolled { height: 58px; background: rgba(3,5,13,0.96); }

    .logo {
      font-family: var(--h); font-weight: 800; font-size: 1.35rem;
      letter-spacing: -0.5px; color: var(--text); cursor: pointer;
      display: flex; align-items: center; gap: 0.5rem;
    }

    .logo-icon {
      width: 30px; height: 30px; border-radius: 8px;
      background: linear-gradient(135deg, var(--violet), var(--primary));
      display: flex; align-items: center; justify-content: center;
      font-size: 0.9rem;
    }

    .logo span { color: var(--violet); }

    .nav-menu {
      display: flex; align-items: center; gap: 0.1rem; list-style: none;
    }

    .nav-menu li button {
      background: none; border: none; cursor: pointer;
      font-family: var(--b); font-size: 0.84rem; font-weight: 500;
      color: var(--muted); padding: 0.45rem 0.9rem; border-radius: 6px;
      transition: all 0.2s; letter-spacing: 0.01em;
    }

    .nav-menu li button:hover,
    .nav-menu li button.active { color: var(--text); background: rgba(99,179,237,0.08); }

    .nav-cta {
      background: linear-gradient(135deg, var(--violet), var(--primary)) !important;
      color: #fff !important; font-weight: 600 !important;
      padding: 0.5rem 1.2rem !important; border-radius: 8px !important;
    }

    .nav-cta:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 4px 20px rgba(159,122,234,0.3); }

    .hamburger {
      display: none; flex-direction: column; gap: 5px;
      background: none; border: none; cursor: pointer; padding: 4px;
    }

    .hamburger span { display: block; width: 22px; height: 2px; background: var(--text); border-radius: 2px; transition: all 0.3s; }

    @media (max-width: 820px) {
      nav { padding: 0 1.2rem; }
      .hamburger { display: flex; }
      .nav-menu {
        display: none; flex-direction: column; align-items: stretch;
        position: absolute; top: 68px; left: 0; right: 0;
        background: rgba(3,5,13,0.98); border-bottom: 1px solid var(--border);
        padding: 1rem; gap: 0.2rem;
      }
      .nav-menu.open { display: flex; }
    }

    /* ── LAYOUT ── */
    section { padding: 110px 0; }
    .container { max-width: 1160px; margin: 0 auto; padding: 0 2rem; }

    /* ── ANIMATIONS ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes glow {
      0%,100% { box-shadow: 0 0 20px rgba(159,122,234,0.2); }
      50%      { box-shadow: 0 0 40px rgba(159,122,234,0.4); }
    }

    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes blink { 0%,100%{opacity:1;} 50%{opacity:0;} }

    .fade-in { opacity: 0; transform: translateY(24px); transition: opacity 0.65s ease, transform 0.65s ease; }
    .fade-in.visible { opacity: 1; transform: translateY(0); }

    /* ── SECTION HEADER ── */
    .eyebrow {
      display: inline-flex; align-items: center; gap: 0.5rem;
      font-family: var(--m); font-size: 0.7rem;
      letter-spacing: 0.18em; text-transform: uppercase;
      color: var(--violet); margin-bottom: 1rem;
    }

    .eyebrow::before {
      content: ''; width: 24px; height: 1px; background: var(--violet);
    }

    .section-h {
      font-family: var(--h); font-weight: 800;
      font-size: clamp(2rem, 3.8vw, 3.2rem);
      line-height: 1.08; letter-spacing: -1px; color: var(--text);
    }

    .section-h .c1 { color: var(--primary); }
    .section-h .c2 { color: var(--violet); }
    .section-h .c3 { color: var(--emerald); }

    .section-sub {
      font-size: 1rem; color: var(--muted); line-height: 1.8;
      max-width: 560px; margin-top: 1rem;
    }

    /* ── CARDS ── */
    .card {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 12px; padding: 1.8rem;
      transition: all 0.3s; position: relative; overflow: hidden;
    }

    .card::before {
      content: ''; position: absolute; inset: 0; border-radius: 12px;
      background: radial-gradient(circle at 50% 0%, rgba(159,122,234,0.06) 0%, transparent 60%);
      opacity: 0; transition: opacity 0.3s;
    }

    .card:hover { border-color: var(--border2); transform: translateY(-3px); }
    .card:hover::before { opacity: 1; }

    /* ── PILL ── */
    .pill {
      display: inline-flex; align-items: center; gap: 0.4rem;
      padding: 0.25rem 0.75rem; border-radius: 20px;
      font-family: var(--m); font-size: 0.68rem; letter-spacing: 0.06em;
      font-weight: 600;
    }

    .pill.blue   { background: rgba(99,179,237,0.1);  color: var(--primary); border: 1px solid rgba(99,179,237,0.2); }
    .pill.violet { background: rgba(159,122,234,0.1); color: var(--violet);  border: 1px solid rgba(159,122,234,0.2); }
    .pill.green  { background: rgba(72,187,120,0.1);  color: var(--emerald); border: 1px solid rgba(72,187,120,0.2); }
    .pill.amber  { background: rgba(246,173,85,0.1);  color: var(--amber);   border: 1px solid rgba(246,173,85,0.2); }
    .pill.rose   { background: rgba(252,129,129,0.1); color: var(--rose);    border: 1px solid rgba(252,129,129,0.2); }

    /* ── BTNS ── */
    .btn {
      display: inline-flex; align-items: center; gap: 0.5rem;
      padding: 0.75rem 1.8rem; border-radius: 8px;
      font-family: var(--b); font-weight: 600; font-size: 0.9rem;
      cursor: pointer; border: none; transition: all 0.25s;
      letter-spacing: 0.01em;
    }

    .btn-grad {
      background: linear-gradient(135deg, var(--violet), var(--primary));
      color: #fff;
    }

    .btn-grad:hover { opacity: 0.9; transform: translateY(-2px); box-shadow: 0 8px 30px rgba(159,122,234,0.35); }

    .btn-ghost {
      background: transparent; color: var(--text);
      border: 1px solid var(--border2) !important;
    }

    .btn-ghost:hover { background: rgba(99,179,237,0.07); border-color: var(--primary) !important; }

    /* ── HERO ── */
    .hero {
      min-height: 100vh; display: flex; align-items: center;
      padding: 130px 0 80px; position: relative;
    }

    .hero-grid {
      display: grid; grid-template-columns: 1.1fr 0.9fr;
      gap: 4rem; align-items: center;
    }

    @media (max-width: 900px) { .hero-grid { grid-template-columns: 1fr; } }

    .hero-badge {
      display: inline-flex; align-items: center; gap: 0.6rem;
      background: rgba(159,122,234,0.08); border: 1px solid rgba(159,122,234,0.2);
      border-radius: 20px; padding: 0.35rem 1rem 0.35rem 0.5rem;
      font-family: var(--m); font-size: 0.72rem; color: var(--violet);
      margin-bottom: 1.8rem; animation: fadeUp 0.5s ease both;
    }

    .badge-chip {
      background: linear-gradient(135deg, var(--violet), var(--primary));
      color: #fff; font-size: 0.65rem; font-weight: 700;
      padding: 0.15rem 0.5rem; border-radius: 10px; letter-spacing: 0.05em;
    }

    .hero h1 {
      font-family: var(--h); font-weight: 800;
      font-size: clamp(2.8rem, 5.5vw, 5rem);
      line-height: 1.02; letter-spacing: -2px;
      animation: fadeUp 0.5s 0.1s ease both;
    }

    .hero h1 .gd {
      background: linear-gradient(135deg, var(--primary) 0%, var(--violet) 50%, var(--emerald) 100%);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .hero p {
      font-size: 1.05rem; color: var(--muted); line-height: 1.8;
      max-width: 500px; margin-top: 1.4rem;
      animation: fadeUp 0.5s 0.2s ease both;
    }

    .hero-actions {
      display: flex; gap: 1rem; margin-top: 2.5rem; flex-wrap: wrap;
      animation: fadeUp 0.5s 0.3s ease both;
    }

    .hero-tags {
      display: flex; gap: 0.6rem; margin-top: 2rem; flex-wrap: wrap;
      animation: fadeUp 0.5s 0.4s ease both;
    }

    /* ── HERO VISUAL ── */
    .hero-vis {
      animation: fadeUp 0.6s 0.3s ease both;
    }

    .mol-card {
      background: var(--card); border: 1px solid var(--border2);
      border-radius: 16px; padding: 1.5rem;
      box-shadow: 0 0 60px rgba(159,122,234,0.1);
      animation: glow 4s infinite;
    }

    .mol-header {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 1.2rem;
    }

    .mol-title { font-family: var(--m); font-size: 0.72rem; color: var(--primary); letter-spacing: 0.1em; }

    .mol-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--emerald); box-shadow: 0 0 8px var(--emerald); animation: blink 1.8s infinite; }

    .mol-output { display: flex; flex-direction: column; gap: 0.7rem; }

    .mol-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 0.6rem 0.8rem; background: var(--surface);
      border-radius: 8px; border: 1px solid var(--border);
    }

    .mol-row-label { font-size: 0.82rem; color: var(--muted); }

    .mol-bar-wrap { flex: 1; margin: 0 0.8rem; height: 4px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; }

    .mol-bar { height: 100%; border-radius: 2px; transition: width 1s ease; }

    .mol-val { font-family: var(--m); font-size: 0.72rem; font-weight: 600; min-width: 36px; text-align: right; }

    /* ── STATS ── */
    .stats-row {
      background: var(--surface); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
      padding: 2.5rem 0;
    }

    .stats-inner { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1rem; text-align: center; }
    @media (max-width: 700px) { .stats-inner { grid-template-columns: repeat(2, 1fr); } }

    .stat-n { font-family: var(--h); font-size: 2.2rem; font-weight: 800; letter-spacing: -1px; }
    .stat-l { font-size: 0.78rem; color: var(--muted); margin-top: 0.2rem; letter-spacing: 0.05em; }

    /* ── USE CASES ── */
    .uc-tabs { display: flex; gap: 0.6rem; margin: 2.5rem 0; flex-wrap: wrap; }

    .uc-tab {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 8px; padding: 0.7rem 1.2rem;
      font-family: var(--b); font-size: 0.84rem; font-weight: 500;
      color: var(--muted); cursor: pointer; transition: all 0.22s;
      display: flex; align-items: center; gap: 0.5rem;
    }

    .uc-tab:hover { border-color: var(--border2); color: var(--text); }

    .uc-tab.active {
      background: rgba(159,122,234,0.1); border-color: var(--violet);
      color: var(--violet); font-weight: 600;
    }

    .uc-panel { display: none; }
    .uc-panel.active { display: block; }

    /* ── TWO COL DEMO ── */
    .demo-grid { display: grid; grid-template-columns: 1fr 1.2fr; gap: 2rem; align-items: start; }
    @media (max-width: 860px) { .demo-grid { grid-template-columns: 1fr; } }

    /* ── INPUTS ── */
    .inp-group { margin-bottom: 1rem; }
    .inp-label { font-family: var(--m); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); display: block; margin-bottom: 0.35rem; }
    .inp, .sel, .tarea {
      width: 100%; background: var(--bg); border: 1px solid var(--border);
      border-radius: 8px; padding: 0.7rem 1rem; color: var(--text);
      font-family: var(--b); font-size: 0.88rem; outline: none; transition: border-color 0.2s;
      -webkit-appearance: none;
    }
    .inp:focus, .sel:focus, .tarea:focus { border-color: var(--violet); box-shadow: 0 0 0 3px rgba(159,122,234,0.1); }
    .tarea { height: 90px; resize: vertical; font-family: var(--m); font-size: 0.78rem; }

    /* ── RUN BTN ── */
    .run-btn {
      width: 100%; margin-top: 0.8rem;
      background: linear-gradient(135deg, var(--violet), var(--primary));
      color: #fff; border: none; border-radius: 8px;
      padding: 0.85rem; font-family: var(--b); font-weight: 700;
      font-size: 0.9rem; cursor: pointer; transition: all 0.3s;
      display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    }

    .run-btn:hover { opacity: 0.9; box-shadow: 0 6px 24px rgba(159,122,234,0.35); transform: translateY(-1px); }
    .run-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

    .spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; }

    /* ── RESULT PANEL ── */
    .result-panel {
      background: var(--surface); border: 1px solid var(--border);
      border-radius: 12px; overflow: hidden;
    }

    .result-panel::before {
      content: ''; display: block; height: 2px;
      background: linear-gradient(90deg, var(--violet), var(--primary), var(--emerald));
    }

    .result-inner { padding: 1.6rem; }

    .result-title {
      font-family: var(--m); font-size: 0.7rem; letter-spacing: 0.15em;
      text-transform: uppercase; color: var(--violet); margin-bottom: 1.2rem;
      display: flex; align-items: center; gap: 0.5rem;
    }

    .result-title::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--emerald); box-shadow: 0 0 6px var(--emerald); animation: blink 1.5s infinite; }

    .r-section { margin-bottom: 1.4rem; }
    .r-sec-title { font-family: var(--m); font-size: 0.67rem; text-transform: uppercase; letter-spacing: 0.14em; color: var(--muted); margin-bottom: 0.6rem; padding-bottom: 0.4rem; border-bottom: 1px solid var(--border); }

    .r-row { display: flex; align-items: center; justify-content: space-between; padding: 0.32rem 0; border-bottom: 1px solid rgba(255,255,255,0.03); font-size: 0.84rem; }
    .r-row .rk { color: var(--muted); }
    .r-row .rv { font-weight: 500; }
    .rv.good  { color: var(--emerald); }
    .rv.warn  { color: var(--amber); }
    .rv.bad   { color: var(--rose); }
    .rv.info  { color: var(--primary); }
    .rv.viol  { color: var(--violet); }

    .r-bar-row { margin: 0.4rem 0; }
    .r-bar-label { display: flex; justify-content: space-between; font-size: 0.78rem; margin-bottom: 0.25rem; }
    .r-bar-label .rk { color: var(--muted); }
    .r-bar-track { height: 5px; background: rgba(255,255,255,0.06); border-radius: 3px; overflow: hidden; }
    .r-bar-fill  { height: 100%; border-radius: 3px; transition: width 0.8s ease; }

    .r-finding {
      font-size: 0.84rem; color: var(--muted); line-height: 1.7;
      padding: 0.75rem; background: rgba(159,122,234,0.05);
      border-left: 2px solid var(--violet); border-radius: 0 6px 6px 0;
      margin-bottom: 0.5rem;
    }

    .r-finding strong { color: var(--text); }

    /* ── SERVICES GRID ── */
    .srv-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.4rem; margin-top: 3.5rem; }
    @media (max-width: 900px) { .srv-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 560px) { .srv-grid { grid-template-columns: 1fr; } }

    .srv-card {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 12px; padding: 1.8rem; transition: all 0.3s;
      position: relative; overflow: hidden; cursor: default;
    }

    .srv-card::after {
      content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
      opacity: 0; transition: opacity 0.3s;
    }

    .srv-card:hover { transform: translateY(-4px); border-color: var(--border2); }
    .srv-card:hover::after { opacity: 1; }

    .srv-card.c1::after { background: var(--primary); }
    .srv-card.c2::after { background: var(--violet); }
    .srv-card.c3::after { background: var(--emerald); }
    .srv-card.c4::after { background: var(--amber); }
    .srv-card.c5::after { background: var(--rose); }
    .srv-card.c6::after { background: linear-gradient(90deg, var(--violet), var(--primary)); }

    .srv-icon { font-size: 2rem; margin-bottom: 1rem; display: block; }
    .srv-card h3 { font-family: var(--h); font-size: 1.15rem; font-weight: 700; margin-bottom: 0.6rem; letter-spacing: -0.3px; }
    .srv-card p { font-size: 0.85rem; color: var(--muted); line-height: 1.7; }
    .srv-pills { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-top: 1rem; }

    /* ── PIPELINE ── */
    .pipeline { display: flex; gap: 0; margin-top: 3.5rem; position: relative; }
    .pipeline::before { content: ''; position: absolute; top: 28px; left: 14%; right: 14%; height: 1px; background: linear-gradient(90deg, transparent, var(--violet), var(--primary), var(--emerald), transparent); }
    @media (max-width: 700px) { .pipeline { flex-direction: column; } .pipeline::before { display: none; } }

    .pip-step { flex: 1; text-align: center; padding: 0 1rem; }
    .pip-num {
      width: 56px; height: 56px; border-radius: 50%;
      background: var(--card); border: 1px solid var(--border2);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 1.2rem; font-family: var(--m); font-size: 0.9rem;
      color: var(--violet); position: relative; z-index: 1;
    }

    .pip-step h4 { font-family: var(--h); font-size: 1rem; font-weight: 700; margin-bottom: 0.4rem; }
    .pip-step p  { font-size: 0.8rem; color: var(--muted); line-height: 1.6; }

    /* ── ROI ── */
    .roi-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.2rem; margin-top: 3.5rem; }
    @media (max-width: 700px) { .roi-grid { grid-template-columns: 1fr; } }

    .roi-card {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 12px; padding: 1.6rem; display: flex;
      align-items: flex-start; gap: 1rem; transition: all 0.3s;
    }

    .roi-card:hover { border-color: var(--border2); transform: translateY(-2px); }

    .roi-num {
      font-family: var(--h); font-size: 2rem; font-weight: 800;
      line-height: 1; min-width: 54px;
    }

    .roi-card h4 { font-family: var(--h); font-size: 1rem; font-weight: 700; margin-bottom: 0.3rem; }
    .roi-card p  { font-size: 0.82rem; color: var(--muted); line-height: 1.6; }

    /* ── CONTACT ── */
    .contact-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 3.5rem; margin-top: 3.5rem; }
    @media (max-width: 860px) { .contact-grid { grid-template-columns: 1fr; } }

    .contact-detail { display: flex; align-items: flex-start; gap: 0.9rem; margin-bottom: 1.4rem; }
    .cicon { width: 38px; height: 38px; border-radius: 8px; background: rgba(159,122,234,0.1); border: 1px solid rgba(159,122,234,0.2); display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
    .clabel { font-size: 0.72rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.1em; font-family: var(--m); }
    .cval   { font-size: 0.92rem; color: var(--primary); margin-top: 0.1rem; }

    .cf {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 14px; padding: 2.2rem; position: relative; overflow: hidden;
    }

    .cf::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, var(--violet), var(--primary)); }

    .cf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
    @media (max-width: 560px) { .cf-row { grid-template-columns: 1fr; } }

    .cf-group { margin-bottom: 1rem; }
    .cf-label { font-family: var(--m); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); display: block; margin-bottom: 0.35rem; }

    .cf-success { text-align: center; padding: 3rem 1rem; }
    .cf-success .ck { font-size: 3.5rem; display: block; margin-bottom: 1rem; }
    .cf-success h4 { font-family: var(--h); font-size: 1.8rem; color: var(--emerald); }
    .cf-success p  { color: var(--muted); font-size: 0.9rem; margin-top: 0.5rem; }

    /* ── FOOTER ── */
    footer { background: var(--surface); border-top: 1px solid var(--border); padding: 4rem 0 2rem; }

    .ft { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 3rem; margin-bottom: 3rem; }
    @media (max-width: 768px) { .ft { grid-template-columns: 1fr 1fr; gap: 2rem; } }

    .ft-brand p { font-size: 0.84rem; color: var(--muted); line-height: 1.7; margin-top: 0.8rem; }
    .ft-col h5 { font-family: var(--m); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.14em; color: var(--violet); margin-bottom: 1.1rem; }
    .ft-col ul { list-style: none; }
    .ft-col ul li { margin-bottom: 0.55rem; }
    .ft-col ul li button { background: none; border: none; cursor: pointer; font-family: var(--b); font-size: 0.84rem; color: var(--muted); transition: color 0.2s; padding: 0; }
    .ft-col ul li button:hover { color: var(--primary); }

    .ft-bottom { border-top: 1px solid var(--border); padding-top: 2rem; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
    .ft-bottom p { font-size: 0.8rem; color: var(--muted); }

    /* ── HIGHLIGHT INFO BOX ── */
    .info-box {
      background: rgba(99,179,237,0.05); border: 1px solid rgba(99,179,237,0.15);
      border-radius: 10px; padding: 1.4rem 1.8rem; margin-top: 2rem;
    }

    .info-box p { font-size: 0.88rem; color: var(--muted); line-height: 1.7; }
    .info-box strong { color: var(--primary); }

    /* ── PLACEHOLDER (no result yet) ── */
    .empty-result {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; padding: 4rem 2rem; text-align: center;
      color: var(--muted); gap: 0.8rem;
    }

    .empty-result .ei { font-size: 3rem; opacity: 0.3; }
    .empty-result p   { font-size: 0.85rem; line-height: 1.6; }
  `}</style>
);

// ── FADE-IN OBSERVER ──────────────────────────────────────────────────────────
function useFadeIn() {
  useEffect(() => {
    const els = document.querySelectorAll(".fade-in");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  });
}

// ── HERO MOLECULE VISUAL ──────────────────────────────────────────────────────
function MolVis() {
  const rows = [
    { label: "Efficacy",        val: 87, color: "#48BB78", cls: "good" },
    { label: "Binding Affinity",val: 74, color: "#63B3ED", cls: "info" },
    { label: "Stability",       val: 91, color: "#9F7AEA", cls: "viol" },
    { label: "Toxicity Risk",   val: 18, color: "#FC8181", cls: "bad"  },
    { label: "ADMET Score",     val: 82, color: "#F6AD55", cls: "warn" },
  ];

  return (
    <div className="mol-card">
      <div className="mol-header">
        <span className="mol-title">⬡ QSAR PREDICTION ENGINE</span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <div className="mol-dot" />
          <span style={{ fontSize: "0.68rem", color: "var(--muted)", fontFamily: "var(--m)" }}>LIVE</span>
        </div>
      </div>

      <div style={{ marginBottom: "1rem", padding: "0.8rem", background: "var(--surface)", borderRadius: "8px", fontFamily: "var(--m)", fontSize: "0.72rem", color: "var(--muted)" }}>
        <span style={{ color: "var(--emerald)" }}>INPUT: </span>
        <span style={{ color: "var(--primary)" }}>C1=CC=C(C=C1)NC(=O)CN2CCN...</span>
      </div>

      <div className="mol-output">
        {rows.map(r => (
          <div className="mol-row" key={r.label}>
            <span className="mol-row-label">{r.label}</span>
            <div className="mol-bar-wrap">
              <div className="mol-bar" style={{ width: `${r.val}%`, background: r.color }} />
            </div>
            <span className="mol-val" style={{ color: r.color }}>{r.val}%</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "1rem", padding: "0.7rem 0.9rem", background: "rgba(72,187,120,0.08)", borderRadius: "6px", border: "1px solid rgba(72,187,120,0.15)" }}>
        <span style={{ fontFamily: "var(--m)", fontSize: "0.7rem", color: "var(--emerald)" }}>
          ✓ AI Recommendation: HIGH POTENTIAL CANDIDATE — Low toxicity + High efficacy profile
        </span>
      </div>
    </div>
  );
}

// ── HOME ──────────────────────────────────────────────────────────────────────
function Home({ navigate }) {
  useFadeIn();
  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div>
              <div className="hero-badge">
                <span className="badge-chip">NEW</span>
                Agentic AI for Pharma & Healthcare
              </div>
              <h1>
                The <span className="gd">Intelligence</span><br />
                Layer for Drug<br />
                Discovery
              </h1>
              <p>
                AgentNexusAI deploys autonomous AI agents across pharmaceutical research, clinical trials, and molecular analysis — turning weeks of research into hours of insight.
              </p>
              <div className="hero-actions">
                <button className="btn btn-grad" onClick={() => navigate("showcase")}>Explore Demos</button>
                <button className="btn btn-ghost" onClick={() => navigate("contact")}>Book a Call</button>
              </div>
              <div className="hero-tags">
                {["QSAR Prediction", "Drug Discovery", "Clinical Trial AI", "Mutation Analysis", "Precision Medicine"].map(t => (
                  <span className="pill violet" key={t}>{t}</span>
                ))}
              </div>
            </div>
            <div className="hero-vis">
              <MolVis />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="stats-row">
        <div className="container">
          <div className="stats-inner">
            {[
              { n: "10×",  l: "Faster Analysis",  c: "var(--primary)" },
              { n: "95%",  l: "Prediction Accuracy", c: "var(--violet)" },
              { n: "72hr", l: "Trial Matching",    c: "var(--emerald)" },
              { n: "6×",   l: "ROI on Research",   c: "var(--amber)" },
              { n: "∞",    l: "Scalable Agents",   c: "var(--rose)" },
            ].map(s => (
              <div key={s.l}>
                <div className="stat-n" style={{ color: s.c }}>{s.n}</div>
                <div className="stat-l">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* OVERVIEW */}
      <section>
        <div className="container">
          <div className="fade-in">
            <div className="eyebrow">Core Platform</div>
            <div className="section-h">What <span className="c2">AgentNexusAI</span> Does</div>
            <p className="section-sub">Three autonomous agent pipelines covering the full drug discovery and clinical research lifecycle.</p>
          </div>
          <div className="srv-grid">
            {[
              { c: "c1", icon: "⬡", title: "Molecular AI", sub: "blue", tag: "QSAR · ADMET · Docking", desc: "Predict efficacy, toxicity, stability and binding affinity from molecular structures using deep QSAR models." },
              { c: "c2", icon: "🧬", title: "Mutation Impact", sub: "violet", tag: "Genomics · Proteomics", desc: "Analyse gene mutations → protein structure changes → candidate drug predictions with research paper summaries." },
              { c: "c3", icon: "🏥", title: "Clinical Trial AI", sub: "green", tag: "Matching · Prediction", desc: "Automate patient matching, adverse event prediction, dropout forecasting and protocol optimisation." },
            ].map(s => (
              <div className={`srv-card ${s.c} fade-in`} key={s.title}>
                <span className="srv-icon">{s.icon}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <div className="srv-pills">
                  <span className={`pill ${s.sub}`}>{s.tag}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "2rem" }}>
            <button className="btn btn-ghost" onClick={() => navigate("services")}>See All Services →</button>
          </div>
        </div>
      </section>
    </>
  );
}

// ── ABOUT ─────────────────────────────────────────────────────────────────────
function About({ navigate }) {
  useFadeIn();
  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="eyebrow">About Us</div>
          <div className="section-h">Built for <span className="c1">Researchers</span>,<br />Powered by <span className="c2">Agents</span></div>
          <p className="section-sub">AgentNexusAI was founded to eliminate the bottlenecks slowing pharmaceutical research — from manual literature review to trial enrollment delays.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem", marginTop: "4rem", alignItems: "start" }} className="fade-in">
          <div>
            <div className="card" style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontFamily: "var(--m)", fontSize: "0.7rem", color: "var(--violet)", marginBottom: "1rem", letterSpacing: "0.1em" }}>AGENT NEXUS // SYSTEM BOOT</div>
              {[
                ["→", "loading", "molecular-engine v3.1"],
                ["→", "init", "qsar-predictor [OK]"],
                ["→", "connect", "pubmed-api [LIVE]"],
                ["→", "spawn", "trial-matching-agent"],
                ["→", "start", "mutation-analyser [READY]"],
                ["✓", "status", "all agents operational"],
              ].map(([s, k, v], i) => (
                <div key={i} style={{ fontFamily: "var(--m)", fontSize: "0.76rem", lineHeight: "2.2", color: i === 5 ? "var(--emerald)" : "var(--muted)" }}>
                  <span style={{ color: i === 5 ? "var(--emerald)" : "var(--violet)", marginRight: "0.5rem" }}>{s}</span>
                  <span style={{ color: "var(--primary)", marginRight: "0.5rem" }}>{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>

            <div className="info-box">
              <p><strong>Our mission:</strong> Compress the drug discovery timeline from years to months using autonomous AI agents that reason, plan, and act across molecular biology, clinical data, and scientific literature.</p>
            </div>
          </div>

          <div>
            <h3 style={{ fontFamily: "var(--h)", fontSize: "1.4rem", marginBottom: "1rem" }}>Why AgentNexusAI</h3>
            <p style={{ color: "var(--muted)", fontSize: "0.93rem", lineHeight: "1.8", marginBottom: "1.5rem" }}>
              Traditional drug discovery takes 12–15 years and $2.6B on average. Our agentic AI platform targets the highest-cost, highest-delay bottlenecks: target identification, ADMET prediction, candidate screening, and trial matching.
            </p>
            {[
              { icon: "🔬", t: "Deep Science Foundation", d: "Built on peer-reviewed QSAR, SAR, and molecular docking research." },
              { icon: "⚡", t: "Agentic Architecture",    d: "Multi-agent orchestration via Python FastAPI with parallel reasoning pipelines." },
              { icon: "🔒", t: "HIPAA-Aware Design",      d: "Privacy-first architecture suitable for clinical and genomic data." },
              { icon: "🤝", t: "Biotech-Ready",           d: "Designed for pharma partnerships, grants, and investor presentations." },
            ].map(f => (
              <div key={f.t} style={{ display: "flex", gap: "0.9rem", padding: "0.9rem 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ width: "34px", height: "34px", borderRadius: "8px", background: "rgba(159,122,234,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{f.icon}</div>
                <div>
                  <strong style={{ fontSize: "0.9rem", display: "block", marginBottom: "0.2rem" }}>{f.t}</strong>
                  <span style={{ fontSize: "0.83rem", color: "var(--muted)" }}>{f.d}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pipeline */}
        <div style={{ marginTop: "5rem" }} className="fade-in">
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <div className="eyebrow" style={{ justifyContent: "center" }}>How It Works</div>
            <div className="section-h" style={{ textAlign: "center" }}>The <span className="c2">Agentic</span> Pipeline</div>
          </div>
          <div className="pipeline">
            {[
              { n: "01", t: "INPUT", d: "Gene mutation, protein sequence, molecule SMILES, or clinical brief" },
              { n: "02", t: "ANALYSE", d: "Agents parse, classify and query scientific databases autonomously" },
              { n: "03", t: "PREDICT", d: "QSAR, toxicity, efficacy and protein impact models run in parallel" },
              { n: "04", t: "REPORT", d: "Full AI report with recommendations, literature citations, and candidates" },
            ].map(s => (
              <div className="pip-step" key={s.n}>
                <div className="pip-num">{s.n}</div>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── SERVICES ──────────────────────────────────────────────────────────────────
function Services({ navigate }) {
  useFadeIn();
  const services = [
    { c: "c1", icon: "⬡", color: "blue",   title: "QSAR Automation",           tag: "Highest ROI", desc: "Automated Quantitative Structure-Activity Relationship modelling. Feed in molecular structures, get back predicted biological activity, toxicity, and drug-likeness scores." },
    { c: "c2", icon: "🧬", color: "violet", title: "Mutation Impact Prediction", tag: "Research Core", desc: "Input gene mutations or protein sequences — AI predicts downstream protein changes, identifies candidate drugs, and summarises relevant research papers automatically." },
    { c: "c3", icon: "💊", color: "green",  title: "Drug Repurposing AI",        tag: "Fast ROI", desc: "Identify existing approved drugs with potential activity against new targets. Cuts development timelines and cost by leveraging known safety profiles." },
    { c: "c4", icon: "🏥", color: "amber",  title: "Clinical Trial Optimisation", tag: "Enterprise Value", desc: "Autonomous agents for patient matching, inclusion/exclusion validation, adverse event prediction, dropout forecasting, and protocol optimisation." },
    { c: "c5", icon: "📚", color: "rose",   title: "AI Literature Mining",       tag: "Research Automation", desc: "Continuously scans PubMed, bioRxiv, and clinical registries. Extracts, summarises, and cross-references findings relevant to your research question." },
    { c: "c6", icon: "🎯", color: "violet", title: "Precision Medicine Assistant", tag: "Future Platform", desc: "Combine genomic data, biomarker profiles, and clinical history to recommend personalised treatment pathways with explainable AI reasoning." },
  ];

  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="eyebrow">Services</div>
          <div className="section-h">Six <span className="c1">Automation</span><br />Opportunities</div>
          <p className="section-sub">Each service is a standalone agentic AI system — deploy one or combine all six into a unified drug discovery platform.</p>
        </div>

        <div className="srv-grid">
          {services.map(s => (
            <div className={`srv-card ${s.c} fade-in`} key={s.title}>
              <span className="srv-icon">{s.icon}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <div className="srv-pills">
                <span className={`pill ${s.color}`}>{s.tag}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ROI */}
        <div style={{ marginTop: "5rem" }} className="fade-in">
          <div className="eyebrow">Business Case</div>
          <div className="section-h">Best <span className="c3">ROI</span> Opportunities</div>
          <div className="roi-grid">
            {[
              { n: "01", nc: "var(--primary)",  t: "QSAR Automation",        d: "Eliminates weeks of manual assay work. Biotech labs pay $50K–$500K/year for this capability.", tag: "blue" },
              { n: "02", nc: "var(--violet)",   t: "Mutation Impact AI",      d: "High research demand from oncology, rare disease, and genomics labs globally.", tag: "violet" },
              { n: "03", nc: "var(--emerald)",  t: "Precision Medicine",       d: "Growing payer and hospital demand for personalised treatment recommendation tools.", tag: "green" },
              { n: "04", nc: "var(--amber)",    t: "AI Literature Mining",     d: "Researchers spend 30% of time on literature review. Automating this is immediately valuable.", tag: "amber" },
              { n: "05", nc: "var(--rose)",     t: "Drug Repurposing AI",      d: "Massive shortcut to market — repurposed drugs already have safety data and skip Phase I.", tag: "rose" },
              { n: "06", nc: "var(--violet)",   t: "Clinical Trial Matching",  d: "Sponsors spend $40K+ per recruited patient. AI matching reduces cost and dropout rates significantly.", tag: "violet" },
            ].map(r => (
              <div className="roi-card fade-in" key={r.n}>
                <div className="roi-num" style={{ color: r.nc }}>{r.n}</div>
                <div>
                  <h4>{r.t}</h4>
                  <p>{r.d}</p>
                  <span className={`pill ${r.tag}`} style={{ marginTop: "0.6rem" }}>{r.t}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: "3rem", textAlign: "center" }}>
          <button className="btn btn-grad" onClick={() => navigate("contact")}>Discuss Your Use Case →</button>
        </div>
      </div>
    </section>
  );
}

// ── SHOWCASE (USE CASE DEMOS) ─────────────────────────────────────────────────
function Showcase({ navigate }) {
  useFadeIn();
  const [tab, setTab] = useState(0);

  const tabs = [
    { icon: "⬡", label: "Molecular Change Impact" },
    { icon: "🧬", label: "Mutation-to-Drug Platform" },
    { icon: "🏥", label: "Clinical Trial Agent" },
  ];

  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="eyebrow">Live Demos</div>
          <div className="section-h">Use Case <span className="c2">Showcase</span></div>
          <p className="section-sub">Interactive POC demos of AgentNexusAI's three core agentic systems. Try them live below.</p>
        </div>

        <div className="uc-tabs">
          {tabs.map((t, i) => (
            <button key={i} className={`uc-tab${tab === i ? " active" : ""}`} onClick={() => setTab(i)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className={`uc-panel${tab === 0 ? " active" : ""}`}><MolecularDemo /></div>
        <div className={`uc-panel${tab === 1 ? " active" : ""}`}><MutationDemo /></div>
        <div className={`uc-panel${tab === 2 ? " active" : ""}`}><ClinicalDemo /></div>

        <div style={{ marginTop: "4rem", textAlign: "center" }} className="fade-in">
          <p style={{ color: "var(--muted)", marginBottom: "1.2rem", fontSize: "0.93rem" }}>Want these agents deployed for your research pipeline?</p>
          <button className="btn btn-grad" onClick={() => navigate("contact")}>Get a Custom Demo →</button>
        </div>
      </div>
    </section>
  );
}

// ── DEMO 1: MOLECULAR CHANGE IMPACT ──────────────────────────────────────────
function MolecularDemo() {
  const [form, setForm] = useState({ smiles: "C1=CC=C(C=C1)NC(=O)CN2CCN(CC2)C3=CC=CC=C3", modification: "add_hydroxyl", target: "kinase" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 2000));
    const mods = {
      add_hydroxyl:  { efficacy: 87, tox: 14, stab: 91, admet: 83, bind: 79, change: "+12%", delta: "Hydroxyl addition improves solubility and H-bond donor count, enhancing target binding.", rec: "PROCEED TO LEAD OPTIMISATION" },
      add_fluorine:  { efficacy: 79, tox: 22, stab: 88, admet: 76, bind: 84, change: "+8%",  delta: "Fluorine substitution increases lipophilicity. Mild toxicity flag detected at CF₃ position.", rec: "CONDITIONAL — RECHECK TOXICITY" },
      remove_methyl: { efficacy: 63, tox: 9,  stab: 77, admet: 71, bind: 61, change: "-5%",  delta: "Methyl removal reduces steric bulk. Lower efficacy but improved safety profile.", rec: "USE AS SAFETY FALLBACK CANDIDATE" },
      add_nitrogen:  { efficacy: 91, tox: 31, stab: 85, admet: 78, bind: 89, change: "+18%", delta: "N-addition boosts binding affinity significantly but increases toxicity risk. Dose optimisation required.", rec: "HIGH POTENTIAL — MONITOR TOXICITY" },
    };
    setResult(mods[form.modification] || mods.add_hydroxyl);
    setLoading(false);
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="demo-grid">
      <div className="card fade-in">
        <div style={{ fontFamily: "var(--m)", fontSize: "0.7rem", color: "var(--primary)", marginBottom: "1.2rem", letterSpacing: "0.1em" }}>⬡ MOLECULAR CHANGE IMPACT PREDICTOR</div>

        <div className="inp-group">
          <label className="inp-label">SMILES Structure</label>
          <textarea className="tarea" name="smiles" value={form.smiles} onChange={h} />
        </div>
        <div className="inp-group">
          <label className="inp-label">Modification Type</label>
          <select className="sel" name="modification" value={form.modification} onChange={h}>
            <option value="add_hydroxyl">Add Hydroxyl Group (–OH)</option>
            <option value="add_fluorine">Add Fluorine (–F)</option>
            <option value="remove_methyl">Remove Methyl Group (–CH₃)</option>
            <option value="add_nitrogen">Add Nitrogen (–N)</option>
          </select>
        </div>
        <div className="inp-group">
          <label className="inp-label">Target Protein Class</label>
          <select className="sel" name="target" value={form.target} onChange={h}>
            <option value="kinase">Kinase</option>
            <option value="gpcr">GPCR</option>
            <option value="protease">Protease</option>
            <option value="nuclear">Nuclear Receptor</option>
          </select>
        </div>
        <button className="run-btn" onClick={run} disabled={loading}>
          {loading ? <><div className="spinner" />Running QSAR Analysis…</> : "⚡ Predict Molecular Impact"}
        </button>
      </div>

      <div className="result-panel fade-in">
        {result ? (
          <div className="result-inner">
            <div className="result-title">AI Prediction Report — Molecular Change Impact</div>

            <div className="r-section">
              <div className="r-sec-title">Predicted Properties</div>
              {[
                { k: "Efficacy Change",      v: `${result.efficacy}%`, c: result.efficacy > 75 ? "good" : "warn" },
                { k: "Toxicity Risk",        v: `${result.tox}%`,      c: result.tox < 20 ? "good" : result.tox < 35 ? "warn" : "bad" },
                { k: "Stability Score",      v: `${result.stab}%`,     c: result.stab > 80 ? "good" : "warn" },
                { k: "ADMET Score",          v: `${result.admet}%`,    c: result.admet > 75 ? "good" : "warn" },
                { k: "Binding Affinity",     v: `${result.bind}%`,     c: result.bind > 75 ? "good" : "warn" },
                { k: "Net Change vs Base",   v: result.change,         c: result.change.startsWith("+") ? "good" : "bad" },
              ].map(r => (
                <div className="r-row" key={r.k}>
                  <span className="rk">{r.k}</span>
                  <span className={`rv ${r.c}`}>{r.v}</span>
                </div>
              ))}
            </div>

            <div className="r-section">
              <div className="r-sec-title">ADMET Profile</div>
              {[
                { k: "Absorption", v: Math.round(result.admet * 0.98), c: "#48BB78" },
                { k: "Distribution", v: Math.round(result.stab * 0.85), c: "#63B3ED" },
                { k: "Metabolism", v: Math.round(result.admet * 0.9),  c: "#9F7AEA" },
                { k: "Excretion",  v: Math.round(result.stab * 0.92),  c: "#F6AD55" },
                { k: "Toxicity",   v: 100 - result.tox,                 c: result.tox < 20 ? "#48BB78" : "#FC8181" },
              ].map(b => (
                <div className="r-bar-row" key={b.k}>
                  <div className="r-bar-label"><span className="rk">{b.k}</span><span style={{ color: b.c, fontFamily: "var(--m)", fontSize: "0.72rem" }}>{b.v}%</span></div>
                  <div className="r-bar-track"><div className="r-bar-fill" style={{ width: `${b.v}%`, background: b.c }} /></div>
                </div>
              ))}
            </div>

            <div className="r-section">
              <div className="r-sec-title">AI Analysis</div>
              <div className="r-finding"><strong>Structural Impact:</strong> {result.delta}</div>
              <div className="r-finding"><strong>Drug-likeness:</strong> Lipinski Rule of 5 compliance verified. MW, LogP, HBD, HBA all within acceptable range for oral bioavailability.</div>
            </div>

            <div style={{ padding: "0.9rem", background: "rgba(72,187,120,0.08)", borderRadius: "6px", border: "1px solid rgba(72,187,120,0.2)", textAlign: "center" }}>
              <span style={{ fontFamily: "var(--m)", fontSize: "0.75rem", fontWeight: "700", color: "var(--emerald)" }}>
                ★ RECOMMENDATION: {result.rec}
              </span>
            </div>
          </div>
        ) : (
          <div className="empty-result">
            <span className="ei">⬡</span>
            <p>Configure molecular parameters and click <strong>"Predict Molecular Impact"</strong> to run the QSAR analysis agent.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── DEMO 2: MUTATION-TO-DRUG ──────────────────────────────────────────────────
function MutationDemo() {
  const [form, setForm] = useState({ gene: "BRCA1", mutation: "c.5266dupC", sequence: "MDLSALRVEEV...", disease: "breast_cancer" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 2200));
    const db = {
      breast_cancer: {
        protein: "BRCA1 (Breast Cancer Type 1 Susceptibility Protein)",
        change: "Frameshift mutation → truncated protein at codon 1756. Loss of BRCT domain. DNA repair function severely impaired.",
        candidates: ["Olaparib (PARP inhibitor) — FDA approved for BRCA1+", "Niraparib", "Rucaparib", "Talazoparib"],
        qsar: 84, tox: 21, confidence: 91,
        summary: "BRCA1 5266dupC is a well-characterised pathogenic variant. PARP inhibitors exploit synthetic lethality in BRCA1-deficient cells. High evidence level from 340+ clinical studies.",
      },
      lung_cancer: {
        protein: "EGFR (Epidermal Growth Factor Receptor)",
        change: "Missense mutation L858R → constitutive kinase activation. Increased cell proliferation signalling.",
        candidates: ["Erlotinib", "Gefitinib", "Osimertinib (3rd gen)", "Afatinib"],
        qsar: 88, tox: 18, confidence: 94,
        summary: "EGFR L858R is the most common activating mutation in NSCLC. TKI therapy shows 60–70% response rates. Osimertinib preferred for CNS penetration.",
      },
      melanoma: {
        protein: "BRAF (B-Raf Proto-Oncogene)",
        change: "BRAF V600E substitution → hyperactivated MEK/ERK signalling cascade. Driver mutation in ~50% melanomas.",
        candidates: ["Vemurafenib", "Dabrafenib", "Trametinib (MEK inhibitor)", "Cobimetinib"],
        qsar: 91, tox: 26, confidence: 88,
        summary: "BRAF V600E is the most common BRAF mutation in melanoma. Combination BRAF+MEK inhibition shows superior progression-free survival vs monotherapy.",
      },
    };
    setResult(db[form.disease] || db.breast_cancer);
    setLoading(false);
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="demo-grid">
      <div className="card fade-in">
        <div style={{ fontFamily: "var(--m)", fontSize: "0.7rem", color: "var(--violet)", marginBottom: "1.2rem", letterSpacing: "0.1em" }}>🧬 MUTATION-TO-DRUG IMPACT PLATFORM</div>

        <div className="inp-group">
          <label className="inp-label">Gene Name</label>
          <input className="inp" name="gene" value={form.gene} onChange={h} placeholder="e.g. BRCA1, EGFR, BRAF" />
        </div>
        <div className="inp-group">
          <label className="inp-label">Mutation ID / Variant</label>
          <input className="inp" name="mutation" value={form.mutation} onChange={h} placeholder="e.g. c.5266dupC, L858R" />
        </div>
        <div className="inp-group">
          <label className="inp-label">Disease Context</label>
          <select className="sel" name="disease" value={form.disease} onChange={h}>
            <option value="breast_cancer">Breast Cancer (BRCA1)</option>
            <option value="lung_cancer">Lung Cancer (EGFR)</option>
            <option value="melanoma">Melanoma (BRAF)</option>
          </select>
        </div>
        <div className="inp-group">
          <label className="inp-label">Protein Sequence (partial)</label>
          <textarea className="tarea" name="sequence" value={form.sequence} onChange={h} placeholder="Paste partial amino acid sequence…" />
        </div>
        <button className="run-btn" onClick={run} disabled={loading}>
          {loading ? <><div className="spinner" />Analysing Mutation…</> : "🧬 Run Mutation Analysis"}
        </button>
      </div>

      <div className="result-panel fade-in">
        {result ? (
          <div className="result-inner">
            <div className="result-title">Mutation Impact Report — AgentNexusAI</div>

            <div className="r-section">
              <div className="r-sec-title">Protein Analysis</div>
              <div className="r-row"><span className="rk">Target Protein</span><span className="rv info">{result.protein}</span></div>
              <div className="r-row"><span className="rk">QSAR Score</span><span className={`rv ${result.qsar > 80 ? "good" : "warn"}`}>{result.qsar}%</span></div>
              <div className="r-row"><span className="rk">Toxicity Risk</span><span className={`rv ${result.tox < 25 ? "good" : "warn"}`}>{result.tox}%</span></div>
              <div className="r-row"><span className="rk">AI Confidence</span><span className="rv good">{result.confidence}%</span></div>
            </div>

            <div className="r-section">
              <div className="r-sec-title">Structural Change</div>
              <div className="r-finding"><strong>Protein Impact:</strong> {result.change}</div>
            </div>

            <div className="r-section">
              <div className="r-sec-title">Candidate Drugs</div>
              {result.candidates.map((d, i) => (
                <div className="r-row" key={d}>
                  <span className="rk">Candidate {i + 1}</span>
                  <span className="rv viol">{d}</span>
                </div>
              ))}
            </div>

            <div className="r-section">
              <div className="r-sec-title">Literature Summary</div>
              <div className="r-finding"><strong>AI Research Summary:</strong> {result.summary}</div>
            </div>
          </div>
        ) : (
          <div className="empty-result">
            <span className="ei">🧬</span>
            <p>Enter mutation details and click <strong>"Run Mutation Analysis"</strong> to generate protein impact and candidate drug predictions.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── DEMO 3: CLINICAL TRIAL ────────────────────────────────────────────────────
function ClinicalDemo() {
  const [form, setForm] = useState({ age: "58", diagnosis: "nsclc", ecog: "1", mutations: "EGFR L858R", prior: "2" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 1800));
    setResult({
      match: 94,
      trials: [
        { id: "NCT04789072", name: "MARIPOSA-2 — Osimertinib + Amivantamab", phase: "Phase III", match: "97%", status: "ENROLLING" },
        { id: "NCT05765929", name: "PAPILLON — Amivantamab + Chemo", phase: "Phase III", match: "91%", status: "ENROLLING" },
        { id: "NCT04302025", name: "SAVANNAH — Osimertinib + Savolitinib", phase: "Phase II", match: "86%", status: "ACTIVE" },
      ],
      adverse: "Moderate — EGFR-TKI class effects (rash, diarrhoea, paronychia). Low risk of ILD at current ECOG performance status.",
      dropout: "18%",
      recommendation: "Patient is an excellent candidate for EGFR-targeted therapy. MARIPOSA-2 offers highest match score. Enrol pending PD-L1 and co-mutation screening.",
    });
    setLoading(false);
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="demo-grid">
      <div className="card fade-in">
        <div style={{ fontFamily: "var(--m)", fontSize: "0.7rem", color: "var(--emerald)", marginBottom: "1.2rem", letterSpacing: "0.1em" }}>🏥 CLINICAL TRIAL OPTIMISATION AGENT</div>

        <div className="inp-group">
          <label className="inp-label">Patient Age</label>
          <input className="inp" name="age" value={form.age} onChange={h} placeholder="e.g. 58" type="number" />
        </div>
        <div className="inp-group">
          <label className="inp-label">Primary Diagnosis</label>
          <select className="sel" name="diagnosis" value={form.diagnosis} onChange={h}>
            <option value="nsclc">NSCLC — Non-Small Cell Lung Cancer</option>
            <option value="breast">Breast Cancer HER2+</option>
            <option value="melanoma">Metastatic Melanoma</option>
            <option value="crc">Colorectal Cancer MSI-H</option>
          </select>
        </div>
        <div className="inp-group">
          <label className="inp-label">ECOG Performance Status (0–4)</label>
          <select className="sel" name="ecog" value={form.ecog} onChange={h}>
            {["0 — Fully active", "1 — Restricted strenuous activity", "2 — Ambulatory, self-care only", "3 — Limited self-care"].map((o, i) => (
              <option value={String(i)} key={i}>{o}</option>
            ))}
          </select>
        </div>
        <div className="inp-group">
          <label className="inp-label">Key Mutations / Biomarkers</label>
          <input className="inp" name="mutations" value={form.mutations} onChange={h} placeholder="e.g. EGFR L858R, PD-L1 50%" />
        </div>
        <div className="inp-group">
          <label className="inp-label">Prior Lines of Therapy</label>
          <input className="inp" name="prior" value={form.prior} onChange={h} type="number" placeholder="e.g. 2" />
        </div>
        <button className="run-btn" style={{ background: "linear-gradient(135deg, #48BB78, #63B3ED)" }} onClick={run} disabled={loading}>
          {loading ? <><div className="spinner" />Matching Trials…</> : "🏥 Run Trial Matching Agent"}
        </button>
      </div>

      <div className="result-panel fade-in">
        {result ? (
          <div className="result-inner">
            <div className="result-title">Clinical Trial Matching Report</div>

            <div className="r-section">
              <div className="r-sec-title">Overall Assessment</div>
              <div className="r-row"><span className="rk">Overall Match Score</span><span className="rv good">{result.match}%</span></div>
              <div className="r-row"><span className="rk">Predicted Dropout Risk</span><span className="rv warn">{result.dropout}</span></div>
              <div className="r-row"><span className="rk">Adverse Event Profile</span><span className="rv info">Moderate</span></div>
              <div className="r-row"><span className="rk">Adverse Details</span><span className="rv" style={{ fontSize: "0.78rem", maxWidth: "200px", textAlign: "right" }}>{result.adverse.split("—")[0]}</span></div>
            </div>

            <div className="r-section">
              <div className="r-sec-title">Matched Trials</div>
              {result.trials.map(t => (
                <div key={t.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.8rem", marginBottom: "0.6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                    <span style={{ fontFamily: "var(--m)", fontSize: "0.68rem", color: "var(--primary)" }}>{t.id}</span>
                    <span className="pill green" style={{ fontSize: "0.62rem" }}>{t.match}</span>
                  </div>
                  <div style={{ fontSize: "0.82rem", marginBottom: "0.3rem" }}>{t.name}</div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <span className="pill blue">{t.phase}</span>
                    <span className="pill green">{t.status}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="r-section">
              <div className="r-sec-title">AI Recommendation</div>
              <div className="r-finding"><strong>Agent Assessment:</strong> {result.recommendation}</div>
            </div>
          </div>
        ) : (
          <div className="empty-result">
            <span className="ei">🏥</span>
            <p>Enter patient profile and click <strong>"Run Trial Matching Agent"</strong> to match against active clinical trials and predict outcomes.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── INDUSTRIES ────────────────────────────────────────────────────────────────
function Industries({ navigate }) {
  useFadeIn();
  const inds = [
    { icon: "💊", c: "c1", color: "blue",   title: "Pharmaceutical",    desc: "Drug discovery, lead optimisation, QSAR modelling, and regulatory data automation for pharma R&D." },
    { icon: "🏥", c: "c2", color: "violet", title: "Healthcare & Hospital", desc: "Clinical decision support, patient matching, adverse event monitoring and precision treatment pathways." },
    { icon: "🧬", c: "c3", color: "green",  title: "Biotech & Genomics", desc: "Gene-to-drug pipelines, protein structure analysis, CRISPR target identification, and mutation databases." },
    { icon: "🔬", c: "c4", color: "amber",  title: "Academic Research",  desc: "AI literature mining, hypothesis generation, grant-ready analysis reports and research acceleration." },
    { icon: "⚕️", c: "c5", color: "rose",   title: "CROs & Trial Sponsors", desc: "Clinical trial optimisation, patient recruitment AI, protocol design, and dropout prediction." },
    { icon: "📊", c: "c6", color: "violet", title: "Health-Tech SaaS",   desc: "White-label agentic AI modules for health-tech platforms — API-first, FastAPI-powered, scalable." },
    { icon: "🧪", c: "c1", color: "blue",   title: "Diagnostics & Pathology", desc: "AI-assisted lab result interpretation, biomarker panel analysis, and diagnostic report generation." },
    { icon: "💉", c: "c2", color: "violet", title: "Vaccines & Immunology", desc: "Antigen structure prediction, vaccine candidate screening, and immunogenicity profiling." },
  ];

  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="eyebrow">Sectors We Serve</div>
          <div className="section-h">Industries <span className="c1">Transformed</span><br />by Agentic AI</div>
          <p className="section-sub">AgentNexusAI's platform is designed to plug into any organisation where biology, chemistry, and patient data intersect.</p>
        </div>

        <div className="srv-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {inds.map(i => (
            <div className={`srv-card ${i.c} fade-in`} key={i.title} style={{ padding: "1.4rem" }}>
              <span className="srv-icon" style={{ fontSize: "1.7rem" }}>{i.icon}</span>
              <h3 style={{ fontSize: "1rem" }}>{i.title}</h3>
              <p style={{ fontSize: "0.8rem" }}>{i.desc}</p>
              <div className="srv-pills"><span className={`pill ${i.color}`} style={{ fontSize: "0.62rem", marginTop: "0.6rem" }}>{i.title}</span></div>
            </div>
          ))}
        </div>

        <div className="info-box fade-in" style={{ marginTop: "3rem" }}>
          <p>
            <strong>Platform Vision:</strong> AgentNexusAI is built to evolve from individual use cases into a unified <strong>AI Pharma Copilot</strong> — combining genomics intelligence, drug discovery automation, clinical trial optimisation, and precision medicine into a single agentic platform for the future of healthcare.
          </p>
        </div>

        <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
          <button className="btn btn-grad" onClick={() => navigate("contact")}>Explore Partnership →</button>
        </div>
      </div>
    </section>
  );
}

// ── CONTACT ───────────────────────────────────────────────────────────────────
const EMAILJS_SVC = "service_dpjefdz";
const EMAILJS_TPL = "template_5apkhr7";
const EMAILJS_KEY = "RYKwNo0u1kERWQmhP";

async function sendEmail(params) {
  const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ service_id: EMAILJS_SVC, template_id: EMAILJS_TPL, user_id: EMAILJS_KEY, template_params: params }),
  });
  if (!res.ok) throw new Error("EmailJS error");
}

function Contact() {
  useFadeIn();
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const h = e => { setError(""); setForm(f => ({ ...f, [e.target.name]: e.target.value })); };

  const submit = async e => {
    e.preventDefault(); setSending(true); setError("");
    try {
      await sendEmail({ name: form.name, email: form.email, company: form.company || "N/A", service: form.interest || "General Inquiry", message: form.message, to_email: "info@agennexusai.com", reply_to: form.email });
      setSent(true);
    } catch { setError("Send failed. Please email info@agennexusai.com directly."); }
    finally { setSending(false); }
  };

  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="eyebrow">Get In Touch</div>
          <div className="section-h">Let's Build <span className="c2">Together</span></div>
          <p className="section-sub">Ready to deploy agentic AI in your pharma or healthcare pipeline? We're here to discuss partnerships, demos, and custom builds.</p>
        </div>

        <div className="contact-grid">
          <div className="fade-in">
            <h3 style={{ fontFamily: "var(--h)", fontSize: "1.4rem", marginBottom: "1.5rem" }}>Reach AgentNexusAI</h3>
            {[
              { icon: "✉️", label: "Email", value: "info@agennexusai.com" },
              { icon: "🌐", label: "Website", value: "www.agennexusai.com" },
              { icon: "🤝", label: "Services", value: "QSAR · Drug Discovery · Clinical AI" },
              { icon: "⏱️", label: "Response", value: "Within 24 business hours" },
              { icon: "💼", label: "Partnerships", value: "Biotech · Pharma · Research Institutes" },
            ].map(d => (
              <div className="contact-detail" key={d.label}>
                <div className="cicon">{d.icon}</div>
                <div>
                  <div className="clabel">{d.label}</div>
                  <div className="cval">{d.value}</div>
                </div>
              </div>
            ))}

            <div className="info-box">
              <p><strong>First session is free.</strong> We offer a complimentary 30-minute discovery call to map agentic AI to your specific research pipeline — no commitment required.</p>
            </div>

            <div style={{ marginTop: "2rem" }}>
              <div className="eyebrow">Ideal For</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
                {["Biotech Startups", "Pharma R&D", "Academic Labs", "CROs", "Health-Tech Founders", "Grant Applications"].map(t => (
                  <span className="pill violet" key={t}>{t}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="cf fade-in">
            {sent ? (
              <div className="cf-success">
                <span className="ck">✅</span>
                <h4>MESSAGE SENT!</h4>
                <p>Delivered to info@agennexusai.com<br />We'll respond within 24 hours.</p>
                <button className="btn btn-ghost" style={{ marginTop: "2rem" }} onClick={() => { setSent(false); setForm({ name:"", email:"", company:"", interest:"", message:"" }); }}>Send Another</button>
              </div>
            ) : (
              <form onSubmit={submit}>
                {error && <div style={{ background: "rgba(252,129,129,0.1)", border: "1px solid rgba(252,129,129,0.2)", borderRadius: "8px", padding: "0.8rem 1rem", marginBottom: "1rem", fontSize: "0.84rem", color: "var(--rose)" }}>⚠️ {error}</div>}
                <div className="cf-row">
                  <div className="cf-group">
                    <label className="cf-label">Full Name *</label>
                    <input className="inp" name="name" value={form.name} onChange={h} required placeholder="Your name" />
                  </div>
                  <div className="cf-group">
                    <label className="cf-label">Email *</label>
                    <input className="inp" type="email" name="email" value={form.email} onChange={h} required placeholder="you@org.com" />
                  </div>
                </div>
                <div className="cf-group">
                  <label className="cf-label">Organisation</label>
                  <input className="inp" name="company" value={form.company} onChange={h} placeholder="Your company / institution" />
                </div>
                <div className="cf-group">
                  <label className="cf-label">Area of Interest</label>
                  <select className="sel" name="interest" value={form.interest} onChange={h}>
                    <option value="">Select an area…</option>
                    <option>QSAR Automation</option>
                    <option>Mutation Impact Prediction</option>
                    <option>Drug Repurposing AI</option>
                    <option>Clinical Trial Optimisation</option>
                    <option>AI Literature Mining</option>
                    <option>Precision Medicine Assistant</option>
                    <option>Custom Agentic AI Build</option>
                    <option>Partnership / Investment</option>
                  </select>
                </div>
                <div className="cf-group">
                  <label className="cf-label">Message *</label>
                  <textarea className="tarea" style={{ height: "120px" }} name="message" value={form.message} onChange={h} required placeholder="Describe your project, research question, or partnership interest…" />
                </div>
                <button className="run-btn" type="submit" disabled={sending}>
                  {sending ? <><div className="spinner" />Sending…</> : "Send Message →"}
                </button>
                <p style={{ fontSize: "0.73rem", color: "var(--muted)", marginTop: "0.8rem", textAlign: "center" }}>
                  🔒 Sent securely to info@agennexusai.com
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = p => { setPage(p); setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const navItems = [
    { id: "home",       label: "Home" },
    { id: "about",      label: "About" },
    { id: "services",   label: "Services" },
    { id: "showcase",   label: "Showcase" },
    { id: "industries", label: "Industries" },
    { id: "contact",    label: "Contact", cta: true },
  ];

  return (
    <>
      <G />
      <div className="page">
        <nav className={scrolled ? "scrolled" : ""}>
          <div className="logo" onClick={() => navigate("home")}>
            <div className="logo-icon">⬡</div>
            Agent<span>Nexus</span>AI
          </div>

          <ul className={`nav-menu${menuOpen ? " open" : ""}`}>
            {navItems.map(item => (
              <li key={item.id}>
                <button
                  className={`${page === item.id ? "active" : ""}${item.cta ? " nav-cta" : ""}`}
                  onClick={() => navigate(item.id)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <button className="hamburger" onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </nav>

        {page === "home"       && <Home navigate={navigate} />}
        {page === "about"      && <About navigate={navigate} />}
        {page === "services"   && <Services navigate={navigate} />}
        {page === "showcase"   && <Showcase navigate={navigate} />}
        {page === "industries" && <Industries navigate={navigate} />}
        {page === "contact"    && <Contact />}

        <footer>
          <div className="container">
            <div className="ft">
              <div className="ft-brand">
                <div className="logo" style={{ cursor: "default" }}>
                  <div className="logo-icon">⬡</div>
                  Agent<span>Nexus</span>AI
                </div>
                <p>Agentic AI for pharmaceutical research, drug discovery, clinical trial optimisation, and precision medicine. Built with Python FastAPI and multi-agent orchestration.</p>
              </div>
              <div className="ft-col">
                <h5>Platform</h5>
                <ul>
                  {["QSAR Automation", "Mutation AI", "Drug Repurposing", "Clinical Agent"].map(l => (
                    <li key={l}><button onClick={() => navigate("services")}>{l}</button></li>
                  ))}
                </ul>
              </div>
              <div className="ft-col">
                <h5>Industries</h5>
                <ul>
                  {["Pharmaceutical", "Healthcare", "Biotech", "CROs"].map(l => (
                    <li key={l}><button onClick={() => navigate("industries")}>{l}</button></li>
                  ))}
                </ul>
              </div>
              <div className="ft-col">
                <h5>Company</h5>
                <ul>
                  {[["About", "about"], ["Showcase", "showcase"], ["Services", "services"], ["Contact", "contact"]].map(([l, p]) => (
                    <li key={l}><button onClick={() => navigate(p)}>{l}</button></li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="ft-bottom">
              <p>© {new Date().getFullYear()} AgentNexusAI. All rights reserved.</p>
              <p style={{ fontFamily: "var(--m)", fontSize: "0.73rem", color: "var(--violet)" }}>info@agennexusai.com</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
